import { t } from "@lingui/macro";
import {
  StarProjectDocument,
  type StarProjectMutation,
  type StarProjectMutationVariables,
  UnstarProjectDocument,
  type UnstarProjectMutation,
  type UnstarProjectMutationVariables,
} from "@src/__generated__/graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable, observable } from "mobx";

type StarProjectActionType = "star" | "unstar";

type StarProjectModalOptions = {
  id: string;
  action: StarProjectActionType;
  withMyTasks?: boolean;
};

export class PopoverProjectActionsStore {
  private appStore: AppStore;
  private onAction?: () => void;

  @observable
  starProjectModalState = new DisclosureState<StarProjectModalOptions>();

  private starProjectMutator = new MutationHelper<
    StarProjectMutation,
    StarProjectMutationVariables
  >(StarProjectDocument);

  private unstarProjectMutator = new MutationHelper<
    UnstarProjectMutation,
    UnstarProjectMutationVariables
  >(UnstarProjectDocument);

  constructor(appStore: AppStore, onAction?: () => void) {
    makeObservable(this);
    this.appStore = appStore;
    this.onAction = onAction;
  }

  @computed
  get isStarToggling(): boolean {
    return (
      this.starProjectMutator.isLoading.value ||
      this.unstarProjectMutator.isLoading.value
    );
  }

  @computed
  get isActionStar(): boolean {
    return this.starProjectModalState.additionalData?.action === "star";
  }

  @computed
  get isActionUnstar(): boolean {
    return this.starProjectModalState.additionalData?.action === "unstar";
  }

  @action.bound
  public async confirmStarAction(
    callback?: (nextState: boolean) => void,
  ): Promise<void> {
    const data = this.starProjectModalState.additionalData;
    if (!data) return;

    if (data.action === "star") {
      await this.starProject(data.id);
      callback?.(true);
    } else {
      await this.unstarProject(data.id);
      callback?.(false);
    }
  }

  @action.bound
  private async starProject(id: string): Promise<void> {
    const withMyTasks =
      this.starProjectModalState.additionalData?.withMyTasks ?? false;

    const [_, error] = await this.starProjectMutator.mutate({
      project_id: id,
      options: { with_my_tasks: withMyTasks },
    });

    if (error) return;

    this.appStore.UIStore.toast({
      title: withMyTasks
        ? t`Project starred and assigned tasks to you.`
        : t`Project starred.`,
      status: "success",
    });

    this.onAction?.();
    this.starProjectModalState.onClose();
  }

  @action.bound
  private async unstarProject(id: string): Promise<void> {
    const withMyTasks =
      this.starProjectModalState.additionalData?.withMyTasks ?? false;

    const [_, error] = await this.unstarProjectMutator.mutate({
      project_id: id,
      options: { with_my_tasks: withMyTasks },
    });

    if (error) return;

    this.appStore.UIStore.toast({
      title: withMyTasks
        ? t`Project unstarred and assigned tasks to you.`
        : t`Project unstarred.`,
      status: "success",
    });

    this.onAction?.();
    this.starProjectModalState.onClose();
  }
}
